import { Link } from '@inertiajs/react'
import { Workflow } from 'lucide-react'
import { Avatar } from '@/components/design-system'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { VariantProps } from 'class-variance-authority'
import { badgeVariants } from '@/components/ui/badge'
import { processStatusVariant } from '@/frontend/features/HomeWork/processStatus'
import type { ProcessDetailData } from './types'

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

/**
 * Cabeçalho do detalhe do processo (Onda C, protótipo pd-head): ícone de fluxo
 * + nome + chip de status do lifecycle + responsável (avatar dither do agente)
 * + ações. "Editar" leva à gestão do processo (o formulário completo já existe
 * na show org). "Pausar" não existe como ação de processo no backend hoje —
 * fica desabilitado (honestidade: nada de botão que não faz nada de verdade).
 */
export function ProcessDetailHeader({ process }: { process: ProcessDetailData }) {
  const statusVariant: BadgeVariant = processStatusVariant(process.status)

  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-start">
      <span
        aria-hidden
        className="flex size-[42px] flex-none items-center justify-center rounded-[10px] bg-brand/10 text-brand"
      >
        <Workflow className="size-5" strokeWidth={1.75} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-[19px] font-semibold leading-tight tracking-[-0.015em] text-foreground">{process.name}</h1>
          <Badge variant={statusVariant}>{process.status_label}</Badge>
        </div>
        <p className="mt-1.5 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Avatar name={process.worker_name} hueSeed={process.worker_id} size="xxs" className="flex-none" />
            Responsável: <span className="font-medium text-foreground">{process.worker_name}</span>
          </span>
          {process.objective && (
            <>
              <span aria-hidden className="text-faint-foreground">·</span>
              <span className="truncate">{process.objective}</span>
            </>
          )}
        </p>
      </div>

      {process.can_manage && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link href={process.edit_path}>Editar</Link>
          </Button>
          <Button type="button" variant="outline" size="sm" disabled title="Em breve">
            Pausar
          </Button>
        </div>
      )}
    </header>
  )
}
